import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { useSEO } from '../../hooks/useSEO';

const NFCSecurity = () => {
  useSEO({
    title: "NFC Security: Is Your Digital Business Card Safe? | AM CARDMATE Blog",
    description: "Understanding NFC security, encryption, and how AM CARDMATE protects your digital business card data and privacy.",
    image: "/assets/security.jpg",
    keywords: "NFC security, digital card security, data protection, privacy, encryption, safe networking"
  });

  useEffect(() => {
    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": "NFC Security: Is Your Digital Business Card Safe?",
        "description": "Understanding NFC security, encryption, and data protection",
        "datePublished": "2025-05-20",
        "dateModified": "2025-05-20",
        "author": { "@type": "Organization", "name": "AM CARDMATE Security Team" },
        "publisher": { "@type": "Organization", "name": "AM CARDMATE", "logo": { "@type": "ImageObject", "url": "https://amcardmate.com/assets/logo.svg" } },
        "image": "https://amcardmate.com/assets/security.jpg",
        "mainEntityOfPage": { "@type": "WebPage", "@id": "https://amcardmate.com/blog/nfc-security" }
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [{
          "@type": "Question",
          "name": "Is NFC technology secure?",
          "acceptedAnswer": { "@type": "Answer", "text": "Yes, NFC uses bank-level AES-256 encryption and has a 4cm range limit for security." }
        }]
      }
    ];
    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
  }, []);

  const [activeSecurityFeature, setActiveSecurityFeature] = useState(0);

  const securityFeatures = [
    { title: "Short Range Communication", description: "4cm maximum range prevents unauthorized access", icon: "wifi", details: "NFC's extremely short range is actually a security feature. Unlike Bluetooth or WiFi, you can't accidentally connect from across the room." },
    { title: "Bank-Level Encryption", description: "Same technology that protects your credit card payments", icon: "lock", details: "AES-256 encryption ensures your data is protected with military-grade security standards." },
    { title: "No Personal Data Storage", description: "Only URL links are stored on the chip", icon: "database", details: "Your NFC card only contains a simple web link. All personal information is stored securely in the cloud." },
    { title: "Real-Time Control", description: "Turn your card on/off instantly from your phone", icon: "power-off", details: "Lost your card? Disable it instantly. Found it again? Re-enable it in seconds. You're always in control." }
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="pt-32 md:pt-36 lg:pt-40 pb-20 bg-gradient-to-b from-red-50 via-orange-50/30 to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-red-200/20 to-orange-200/20 rounded-full filter blur-3xl" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto">
            <nav className="flex flex-wrap items-center gap-2 text-caption mb-6">
              <Link to="/" className="hover:text-primary-blue transition-colors">Home</Link>
              <FontAwesomeIcon icon="chevron-right" className="text-xs" />
              <Link to="/blog" className="hover:text-primary-blue transition-colors">Blog</Link>
              <FontAwesomeIcon icon="chevron-right" className="text-xs" />
              <span className="text-primary-blue">NFC Security</span>
            </nav>

            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-100 to-orange-100 rounded-full mb-6">
                <FontAwesomeIcon icon="shield-alt" className="text-red-500" />
                <span className="font-medium text-gray-700 text-body-sm">Security</span>
              </div>

              <h1 className="text-h1 mb-6">
                NFC Security: How Safe Are
                <span className="block text-gradient-blue-pink mt-2">Digital Business Cards?</span>
              </h1>

              <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-caption">
                <span className="flex items-center gap-2">
                  <FontAwesomeIcon icon="user" className="text-red-500" />
                  Security Team
                </span>
                <span className="flex items-center gap-2">
                  <FontAwesomeIcon icon="calendar" className="text-red-500" />
                  May 20, 2025
                </span>
                <span className="flex items-center gap-2">
                  <FontAwesomeIcon icon="clock" className="text-red-500" />
                  5 min read
                </span>
              </div>
            </div>

            <div className="relative rounded-3xl overflow-hidden shadow-2xl mb-12">
              <img src="https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=1200&h=600&fit=crop" alt="NFC Security Illustration" className="w-full h-auto" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            {/* Intro */}
            <p className="text-body-lg text-gray-600 mb-8">
              "Is it safe to tap my phone on that?" That's the question everyone asks when they first encounter NFC technology. And honestly? It's a smart question! In a world where data breaches make headlines daily, being cautious about new technology is just good sense. So let's dive deep into NFC security and put your mind at ease.
            </p>

            <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-6 md:p-8 mb-12">
              <h3 className="text-h4 text-dark mb-4 flex items-center gap-3">
                <FontAwesomeIcon icon="exclamation-triangle" className="text-red-500" />
                The Bottom Line First
              </h3>
              <p className="text-body text-gray-600">
                NFC technology is actually <strong>more secure</strong> than traditional business cards, QR codes, or even Bluetooth connections. The same technology that protects your contactless payments also protects your digital business card. Here's why...
              </p>
            </div>

            {/* Built-In Security Features */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-red-400 to-red-600 rounded-xl flex items-center justify-center text-white">
                  <FontAwesomeIcon icon="shield-alt" />
                </div>
                <h2 className="text-h3 text-dark">Built-In Security Features</h2>
              </div>

              <div className="bg-gray-50 rounded-3xl p-6 md:p-8">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
                  {securityFeatures.map((feature, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveSecurityFeature(index)}
                      className={`p-4 rounded-xl text-left transition-all ${
                        activeSecurityFeature === index
                          ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white shadow-lg'
                          : 'bg-white text-gray hover:bg-gray-100'
                      }`}
                    >
                      <FontAwesomeIcon icon={feature.icon} className={`text-xl mb-2 ${activeSecurityFeature === index ? 'text-white' : 'text-red-500'}`} />
                      <h4 className="text-body-sm font-bold mb-1 leading-tight">{feature.title}</h4>
                      <p className="text-caption opacity-80 hidden sm:block">{feature.description}</p>
                    </button>
                  ))}
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <h3 className="text-h4 text-dark mb-4">{securityFeatures[activeSecurityFeature].title}</h3>
                  <p className="text-body text-gray-600">{securityFeatures[activeSecurityFeature].details}</p>
                </div>
              </div>
            </div>

            {/* Busting Security Myths */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl flex items-center justify-center text-white">
                  <FontAwesomeIcon icon="times-circle" />
                </div>
                <h2 className="text-h3 text-dark">Busting Security Myths</h2>
              </div>

              <div className="space-y-4">
                {[
                  { myth: '"NFC cards can be hacked from far away"', reality: "NFC only works within 4cm. You literally have to touch devices together. It's like saying someone can steal your handshake from across the room – physically impossible!", color: 'border-red-500' },
                  { myth: '"All my personal data is stored on the card"', reality: 'Your NFC card only contains a simple web link (like "amcardmate.com/yourname"). All your actual contact info is stored securely in the cloud, protected by enterprise-grade security.', color: 'border-orange-500' },
                  { myth: '"If I lose my card, my data is compromised"', reality: "Lost your card? Disable it instantly from your phone. The finder gets a dead link, and you can order a replacement that works with your existing profile. Try doing that with paper cards!", color: 'border-yellow-500' }
                ].map((item, i) => (
                  <div key={i} className={`bg-white rounded-2xl p-6 shadow-lg border-l-4 ${item.color} hover:shadow-xl transition-all`}>
                    <h3 className="text-body font-bold text-dark mb-3 flex items-center gap-2">
                      <FontAwesomeIcon icon="times" className="text-red-500" />
                      <span>Myth: {item.myth}</span>
                    </h3>
                    <p className="text-body text-gray-600"><strong>Reality:</strong> {item.reality}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Security Comparison */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center text-white">
                  <FontAwesomeIcon icon="balance-scale" />
                </div>
                <h2 className="text-h3 text-dark">Security Comparison: NFC vs Everything Else</h2>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full bg-white rounded-2xl shadow-lg overflow-hidden">
                  <thead>
                    <tr className="bg-gradient-to-r from-red-500 to-orange-500 text-white">
                      <th className="p-4 text-left text-body font-bold">Security Feature</th>
                      <th className="p-4 text-center text-body font-bold">NFC Cards</th>
                      <th className="p-4 text-center text-body font-bold">Paper Cards</th>
                      <th className="p-4 text-center text-body font-bold">QR Codes</th>
                      <th className="p-4 text-center text-body font-bold hidden md:table-cell">Bluetooth</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { feature: 'Range Limitation', nfc: '4cm only ✅', paper: 'Physical only', qr: 'Visual range', bt: '10-100m ❌', nfcGreen: true, btRed: true },
                      { feature: 'Data Encryption', nfc: 'AES-256 ✅', paper: 'None ❌', qr: 'None ❌', bt: 'Basic', nfcGreen: true, paperRed: true, qrRed: true },
                      { feature: 'Remote Disable', nfc: 'Instant ✅', paper: 'Impossible ❌', qr: 'No ❌', bt: 'No ❌', nfcGreen: true, paperRed: true, qrRed: true, btRed: true },
                      { feature: 'Data on Device', nfc: 'URL only ✅', paper: 'All info ❌', qr: 'All info ❌', bt: 'All info ❌', nfcGreen: true, paperRed: true, qrRed: true, btRed: true }
                    ].map((row, i) => (
                      <tr key={i} className={`border-b hover:bg-gray-50 transition-colors ${i % 2 === 1 ? 'bg-gray-50' : ''}`}>
                        <td className="p-4 text-body font-medium">{row.feature}</td>
                        <td className="p-4 text-center"><span className="text-green-600 font-bold text-body-sm">{row.nfc}</span></td>
                        <td className="p-4 text-center"><span className={`text-body-sm ${row.paperRed ? 'text-red-600' : 'text-gray-600'}`}>{row.paper}</span></td>
                        <td className="p-4 text-center"><span className={`text-body-sm ${row.qrRed ? 'text-red-600' : 'text-gray-600'}`}>{row.qr}</span></td>
                        <td className="p-4 text-center hidden md:table-cell"><span className={`text-body-sm ${row.btRed ? 'text-red-600' : 'text-gray-600'}`}>{row.bt}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* AM CARDMATE Security Promise */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-blue to-accent-purple rounded-xl flex items-center justify-center text-white">
                  <FontAwesomeIcon icon="star" />
                </div>
                <h2 className="text-h3 text-dark">How AM CARDMATE Goes Above & Beyond</h2>
              </div>

              <div className="bg-gradient-to-r from-primary-blue to-accent-purple text-white rounded-3xl p-6 md:p-10 mb-8">
                <h3 className="text-h3 mb-4 text-center text-white">Our Security Promise</h3>
                <p className="text-body text-white/90 mb-8 text-center max-w-3xl mx-auto">
                  We don't just meet security standards – we exceed them. Here's how we protect your professional reputation:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
                  {[
                    { title: 'ISO 27001 Certified', desc: 'International security standard compliance' },
                    { title: 'GDPR Compliant', desc: 'European privacy law protection' },
                    { title: 'SOC 2 Type II', desc: 'Audited security controls' },
                    { title: '24/7 Monitoring', desc: 'Continuous threat detection' }
                  ].map((item, i) => (
                    <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-5 flex items-start gap-4">
                      <FontAwesomeIcon icon="check-circle" className="text-yellow-300 text-xl mt-1 flex-shrink-0" />
                      <div>
                        <div className="font-bold mb-1 text-white">{item.title}</div>
                        <div className="text-white/80 text-body-sm">{item.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Best Practices */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-teal-600 rounded-xl flex items-center justify-center text-white">
                  <FontAwesomeIcon icon="user-shield" />
                </div>
                <h2 className="text-h3 text-dark">Best Practices for Maximum Security</h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {[
                  { title: 'Phone Security', icon: 'mobile-alt', color: 'from-green-500 to-green-600', bg: 'from-green-50 to-green-100', dotColor: 'bg-green-500', items: ['Keep your phone\'s OS updated', 'Use a strong passcode or biometric lock', 'Enable two-factor authentication', 'Review app permissions regularly'] },
                  { title: 'Account Security', icon: 'key', color: 'from-blue-500 to-blue-600', bg: 'from-blue-50 to-blue-100', dotColor: 'bg-blue-500', items: ['Use a unique, strong password', 'Enable login notifications', 'Review connected devices monthly', 'Log out from public computers'] },
                  { title: 'Card Management', icon: 'credit-card', color: 'from-purple-500 to-purple-600', bg: 'from-purple-50 to-purple-100', dotColor: 'bg-purple-500', items: ['Disable cards when not in use', 'Monitor analytics for unusual activity', 'Report lost cards immediately', 'Keep your profile information current'] },
                  { title: 'Privacy Settings', icon: 'eye', color: 'from-orange-500 to-orange-600', bg: 'from-orange-50 to-orange-100', dotColor: 'bg-orange-500', items: ['Control what information is visible', 'Set up professional vs personal profiles', 'Use privacy mode for sensitive events', 'Review analytics sharing settings'] }
                ].map((section, i) => (
                  <div key={i} className={`bg-gradient-to-br ${section.bg} rounded-2xl p-6 md:p-8`}>
                    <div className="flex items-center gap-3 mb-6">
                      <div className={`w-12 h-12 bg-gradient-to-br ${section.color} rounded-lg flex items-center justify-center text-white`}>
                        <FontAwesomeIcon icon={section.icon} className="text-xl" />
                      </div>
                      <h3 className="text-h5 text-dark">{section.title}</h3>
                    </div>
                    <ul className="text-body text-gray-600 space-y-3">
                      {section.items.map((item, j) => (
                        <li key={j} className="flex items-start gap-3">
                          <div className={`w-2 h-2 ${section.dotColor} rounded-full mt-2 flex-shrink-0`}></div>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Future of NFC Security */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-pink-500 rounded-xl flex items-center justify-center text-white">
                  <FontAwesomeIcon icon="rocket" />
                </div>
                <h2 className="text-h3 text-dark">The Future of NFC Security</h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {[
                  { icon: 'fingerprint', title: 'Biometric Authentication', desc: 'Future NFC cards will include fingerprint sensors, ensuring only you can activate your card.', gradient: 'from-yellow-400 to-orange-500' },
                  { icon: 'link', title: 'Blockchain Verification', desc: 'Immutable identity verification ensuring the person you\'re meeting is exactly who they claim to be.', gradient: 'from-blue-400 to-purple-500' },
                  { icon: 'brain', title: 'AI Threat Detection', desc: 'Machine learning algorithms that detect and prevent suspicious activity in real-time.', gradient: 'from-green-400 to-teal-500' }
                ].map((item, i) => (
                  <div key={i} className="bg-white rounded-2xl shadow-lg p-6 md:p-8 hover:shadow-xl transition-all">
                    <div className={`w-16 h-16 bg-gradient-to-br ${item.gradient} rounded-xl flex items-center justify-center mb-6`}>
                      <FontAwesomeIcon icon={item.icon} className="text-2xl text-white" />
                    </div>
                    <h3 className="text-h5 text-dark mb-3">{item.title}</h3>
                    <p className="text-body text-gray-600">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-gradient-to-r from-[#4DA2FF] to-[#DE6161] relative overflow-hidden">
        <div className="absolute inset-0 bg-dots-pattern opacity-10"></div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full filter blur-3xl" />
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center">
            <div className="text-h1 font-bold text-white mb-4">A+</div>
            <h2 className="text-h2 text-white mb-4">Safer Than Your Wallet</h2>
            <p className="text-body-lg text-white/90 mb-8 max-w-2xl mx-auto">
              NFC business cards aren't just as secure as traditional cards – they're exponentially more secure. With bank-level encryption, instant remote control, and minimal data exposure, you're actually <em>safer</em> going digital.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/personal-card" className="btn bg-white text-primary-blue hover:bg-gray-100 transition-all shadow-lg">
                <FontAwesomeIcon icon="shield-alt" />
                <span>Get Secure Digital Card</span>
              </Link>
              <Link to="/nfc-cards" className="btn bg-white/20 text-white hover:bg-white/30 transition-all">
                <FontAwesomeIcon icon="credit-card" />
                <span>Shop NFC Cards</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Author & Related */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="border-t pt-8 mb-12">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center text-white font-bold text-body-lg flex-shrink-0">ST</div>
                <div>
                  <h3 className="text-card-title text-dark">Security Team</h3>
                  <p className="text-body-sm text-gray-500 mb-2">AM CARDMATE Cybersecurity Division</p>
                  <p className="text-body-sm text-gray-600">
                    Former cybersecurity experts from Fortune 500 companies, now dedicated to keeping your digital networking safe. We sleep well at night knowing your data is protected – and you should too!
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-h3 text-dark mb-6">Learn More</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                {[
                  { to: '/blog/what-is-nfc-technology', title: 'What is NFC Technology?', gradient: 'from-blue-400 to-blue-600' },
                  { to: '/blog/future-of-networking', title: 'Future of Networking', gradient: 'from-purple-400 to-pink-600' },
                  { to: '/blog/setting-up-digital-business-card', title: 'Setup Your Digital Card', gradient: 'from-green-400 to-teal-600' }
                ].map((post, i) => (
                  <Link key={i} to={post.to} className={`group ${i === 2 ? 'sm:col-span-2 md:col-span-1' : ''}`}>
                    <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300">
                      <div className={`h-40 bg-gradient-to-br ${post.gradient}`}></div>
                      <div className="p-4">
                        <h4 className="text-body font-bold text-dark group-hover:text-primary-blue transition-colors">{post.title}</h4>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default NFCSecurity;